// Auth slice for managing authentication state

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { User } from '@/types'
import { storageService } from '@/services'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
}

const initialState: AuthState = {
  user: storageService.getUser<User>(),
  token: storageService.getAuthToken(),
  isAuthenticated: storageService.isLoggedIn(),
  isLoading: false,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ user: User; token: string; refreshToken?: string }>
    ) => {
      const { user, token, refreshToken } = action.payload
      state.user = user
      state.token = token
      state.isAuthenticated = true

      // Save to localStorage
      storageService.setUser(user)
      storageService.setAuthToken(token)
      if (refreshToken) {
        storageService.setRefreshToken(refreshToken)
      }
      storageService.setLoggedIn(true)
    },

    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      storageService.setUser(action.payload)
    },

    logout: (state) => {
      state.user = null
      state.token = null
      state.isAuthenticated = false

      // Clear localStorage
      storageService.removeAuthTokens()
      storageService.removeUser()
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
  },
})

export const { setCredentials, setUser, logout, setLoading } = authSlice.actions

export default authSlice.reducer

// Selectors
export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated
export const selectAuthToken = (state: { auth: AuthState }) => state.auth.token
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading
